import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { enUS, es } from 'date-fns/locale';
import { isSameMonth } from 'date-fns';
import { Box, CircularProgress, Divider, Stack, Typography, Unstable_Grid2 as Grid } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  CheckCircle as CheckIcon, InsertChartOutlined as ChartIcon,
  LocalFireDepartment as StreakIcon, CalendarMonth as CalendarIcon
} from '@mui/icons-material';
import SessionCounters from 'components/session/SessionCounters';
import SessionProgressDonut from 'components/session/SessionProgressDonut';
import SessionListItem from 'components/session/SessionListItem';
import { normalizeSessionStatus } from 'common/utils/sessionColor';
import groupSessionSlice from 'store/modules/groupSession';
import sessionSlice from 'store/modules/session';

const getDisplayStatus = (session, currentUserId) => {
  const mine = session.attendances?.find((a) => String(a.user?.id) === String(currentUserId));
  return mine?.status ?? session.status;
};

/**
 * Contenido del progreso de SESIONES (métricas + donut + historial). Se incrusta en la página
 * "Mi progreso" del cliente (sin clientId) y en la ficha del cliente del entrenador (con clientId,
 * que entonces muestra SOLO las sesiones individuales de ESE cliente, sin grupales).
 */
function SessionProgressContent({ clientId = null }) {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { t, i18n } = useTranslation(['sessions']);
  const dateLocale = i18n.language === 'es' ? es : enUS;

  const groupSessions = useSelector((state) => state.groupSession.data.myGroupSessions);
  const individualSessions = useSelector((state) => state.session.data.list) || [];
  const storeStatus = useSelector((state) => state.groupSession.status.general);
  const currentUserId = useSelector((state) => state.auth.user.id);
  const isActivePlus = useSelector((state) => state.auth.user.is_active_plus);

  const isTrainerView = Boolean(clientId);
  const targetUserId = clientId || currentUserId;

  useEffect(() => {
    // Las grupales son del usuario autenticado (Active Plus); en vista de entrenador NO aplican.
    if (!isTrainerView && isActivePlus) dispatch(groupSessionSlice.actions.fetchMyGroupSessions());
    if (targetUserId) dispatch(sessionSlice.actions.list({ pagination: { first: 100, page: 1 }, filters: { client_id: targetUserId } }));
  }, [dispatch, targetUserId, isActivePlus, isTrainerView]);

  const items = useMemo(() => {
    const group = isTrainerView ? [] : groupSessions.map((s) => ({ session: s, displayStatus: getDisplayStatus(s, currentUserId) }));
    const individual = individualSessions.map((s) => ({ session: s, displayStatus: s.status }));
    return [...group, ...individual]
      .map((it) => ({ ...it, norm: normalizeSessionStatus(it.displayStatus, it.session.start_date), date: new Date(it.session.start_date) }))
      .sort((a, b) => b.date - a.date);
  }, [groupSessions, individualSessions, currentUserId, isTrainerView]);

  const metrics = useMemo(() => {
    const now = new Date();
    let completed = 0; let absent = 0; let scheduled = 0; let thisMonth = 0;
    items.forEach((it) => {
      if (it.norm === 'completed') { completed += 1; if (isSameMonth(it.date, now)) thisMonth += 1; }
      else if (it.norm === 'absent') absent += 1;
      else if (it.norm === 'scheduled' || it.norm === 'pending') scheduled += 1;
    });
    let streak = 0;
    const past = items.filter((it) => it.norm === 'completed' || it.norm === 'absent');
    for (let i = 0; i < past.length; i += 1) {
      if (past[i].norm === 'completed') streak += 1; else break;
    }
    const pct = completed + absent > 0 ? Math.round((completed / (completed + absent)) * 100) : 0;
    return { completed, absent, scheduled, thisMonth, streak, pct };
  }, [items]);

  const history = useMemo(() => items.filter((it) => it.norm === 'completed' || it.norm === 'absent'), [items]);

  const counters = [
    { key: 'completed', label: t('statusCompletedPlural', { ns: 'sessions', defaultValue: 'Completadas' }), value: metrics.completed, sub: t('inTotal', { ns: 'sessions', defaultValue: 'En total' }), color: theme.palette.sessionStatus.completed.fg, icon: <CheckIcon /> },
    { key: 'attendance', label: t('attendance', { ns: 'sessions', defaultValue: 'Asistencia' }), value: `${metrics.pct}%`, sub: t('overall', { ns: 'sessions', defaultValue: 'General' }), color: theme.palette.gold.bright, icon: <ChartIcon /> },
    { key: 'streak', label: t('streak', { ns: 'sessions', defaultValue: 'Racha' }), value: metrics.streak, sub: t('streakSub', { ns: 'sessions', defaultValue: 'Sesiones seguidas' }), color: theme.palette.gold.bright, icon: <StreakIcon /> },
    { key: 'month', label: t('thisMonth', { ns: 'sessions', defaultValue: 'Este mes' }), value: metrics.thisMonth, sub: t('completedThisMonth', { ns: 'sessions', defaultValue: 'Completadas' }), color: theme.palette.sessionProximity.far, icon: <CalendarIcon /> }
  ];

  const isLoading = storeStatus === 'loading' && items.length === 0;

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={28} sx={{ color: 'secondary.main' }} /></Box>;
  }

  return (
    <>
      <Box sx={{ mb: 3 }}>
        <SessionCounters counters={counters} />
      </Box>

      <Grid container spacing={2.5} sx={{ mb: 1 }}>
        <Grid xs={12} md={5}>
          <Box sx={{ backgroundColor: 'surface.content', border: '1px solid', borderColor: 'hairline.main', borderRadius: '16px', p: { xs: 2.5, md: 3 }, height: '100%', boxShadow: (th) => th.palette.shadow.card }}>
            <SessionProgressDonut completed={metrics.completed} scheduled={metrics.scheduled} absent={metrics.absent} />
          </Box>
        </Grid>
        <Grid xs={12} md={7}>
          <Box sx={{ backgroundColor: 'surface.content', border: '1px solid', borderColor: 'hairline.main', borderRadius: '16px', p: { xs: 2.5, md: 3 }, height: '100%', boxShadow: (th) => th.palette.shadow.card }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.5 }}>{t('summaryTitle', { ns: 'sessions', defaultValue: 'Resumen' })}</Typography>
            <Stack spacing={1.25}>
              <Stack direction="row" justifyContent="space-between"><Typography variant="body2" color="text.secondary">{t('statusCompletedPlural', { ns: 'sessions', defaultValue: 'Completadas' })}</Typography><Typography variant="body2" sx={{ fontWeight: 800, color: (th) => th.palette.sessionStatus.completed.fg }}>{metrics.completed}</Typography></Stack>
              <Divider />
              <Stack direction="row" justifyContent="space-between"><Typography variant="body2" color="text.secondary">{t('statusScheduledPlural', { ns: 'sessions', defaultValue: 'Programadas' })}</Typography><Typography variant="body2" sx={{ fontWeight: 800, color: (th) => th.palette.sessionProximity.far }}>{metrics.scheduled}</Typography></Stack>
              <Divider />
              <Stack direction="row" justifyContent="space-between"><Typography variant="body2" color="text.secondary">{t('absences', { ns: 'sessions', defaultValue: 'Ausencias' })}</Typography><Typography variant="body2" sx={{ fontWeight: 800, color: (th) => th.palette.sessionStatus.absent.fg }}>{metrics.absent}</Typography></Stack>
              <Divider />
              <Stack direction="row" justifyContent="space-between"><Typography variant="body2" color="text.secondary">{t('streak', { ns: 'sessions', defaultValue: 'Racha' })}</Typography><Typography variant="body2" sx={{ fontWeight: 800, color: (th) => th.palette.gold.bright }}>{metrics.streak} 🔥</Typography></Stack>
            </Stack>
          </Box>
        </Grid>
      </Grid>

      <Typography variant="h6" sx={{ fontWeight: 800, mt: 3, mb: 1.5 }}>{t('sessionHistory', { ns: 'sessions', defaultValue: 'Historial de sesiones' })}</Typography>
      <Stack spacing={1.5}>
        {history.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>{t('noHistory', { ns: 'sessions', defaultValue: 'Aún no tienes sesiones hechas.' })}</Typography>
        ) : history.map(({ session: s, displayStatus }) => (
          <SessionListItem key={s.id} session={s} displayStatus={displayStatus} dateLocale={dateLocale} />
        ))}
      </Stack>
    </>
  );
}

SessionProgressContent.propTypes = { clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) };

export default SessionProgressContent;
